/**
 * Newsletter popup — auto-opens a dialog-component based on trigger settings.
 *
 * Trigger + cooldown config are passed via data attributes so no Liquid
 * interpolation is needed in this file, and the section live-reloads cleanly.
 *
 * Triggers:
 *   delay           — opens after data-delay-seconds seconds
 *   scroll          — opens after the user scrolls data-scroll-percent % of the page
 *   exit_intent     — opens when the cursor leaves the viewport top edge
 *   product_delay   — same as delay, but only on product pages
 *   product_scroll  — same as scroll, but only on product pages
 *   manual          — opens when document dispatches 'ss-newsletter:open'
 *
 * Cooldown: ISO timestamp stored in localStorage under key
 *   ss-modal-popup:<data-section-id>
 * Debug mode: data-debug-always-show="true" bypasses cooldown reads/writes.
 */
class SsModalPopup extends HTMLElement {
  /** @type {import('./dialog.js').DialogComponent | null} */
  #dialogComponent = null;

  /** @type {string} */
  #cooldownKey = '';

  /** @type {(() => void) | null} */
  #unbindTrigger = null;

  /** @type {MutationObserver | null} */
  #drawerObserver = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #retryTimer = null;

  connectedCallback() {
    this.#dialogComponent = this.querySelector('dialog-component');
    this.#cooldownKey = `ss-modal-popup:${this.dataset.sectionId ?? 'default'}`;

    // Write cooldown whenever the dialog closes (ESC, backdrop click, close btn, decline)
    this.#dialogComponent?.addEventListener('dialog:close', () => this.#writeCooldown());

    // After a successful form submit the page reloads with this attribute set —
    // reopen the dialog to show the success message.
    if (this.hasAttribute('data-open-on-load')) {
      requestAnimationFrame(() => this.#open());
      return;
    }

    // Never auto-trigger while the merchant is editing in the theme editor.
    if (window.Shopify?.designMode) return;

    if (this.#isWithinCooldown()) return;

    switch (this.dataset.trigger) {
      case 'delay':
        this.#bindDelay();
        break;
      case 'scroll':
        this.#bindScroll();
        break;
      case 'exit_intent':
        this.#bindExitIntent();
        break;
      case 'product_delay':
        if (this.#isProductPage()) this.#bindDelay();
        break;
      case 'product_scroll':
        if (this.#isProductPage()) this.#bindScroll();
        break;
      case 'manual':
        this.#bindManual();
        break;
    }
  }

  /** True when the popup is rendered on a product template page. */
  #isProductPage() {
    // template.name is "product" on product pages (Shopify resolves
    // product.<handle> templates to the same name).
    return (this.dataset.template ?? '').split('.')[0] === 'product';
  }

  disconnectedCallback() {
    this.#unbindTrigger?.();
    this.#unbindTrigger = null;
    this.#drawerObserver?.disconnect();
    this.#drawerObserver = null;
    if (this.#retryTimer !== null) {
      clearTimeout(this.#retryTimer);
      this.#retryTimer = null;
    }
  }

  /** Returns true if any dialog other than this popup's own is currently open. */
  #isAnyDrawerOpen() {
    const myDialog = this.#dialogComponent?.querySelector('dialog');
    for (const dialog of document.querySelectorAll('dialog[open]')) {
      if (dialog !== myDialog) return true;
    }
    return false;
  }

  /**
   * Watch for all other open dialogs to close, then wait 1–2 s and retry.
   * Safe to call multiple times — a second call is a no-op while an observer
   * is already active.
   */
  #scheduleRetryAfterDrawerClose() {
    if (this.#drawerObserver) return;

    this.#drawerObserver = new MutationObserver(() => {
      if (this.#isAnyDrawerOpen()) return;
      this.#drawerObserver?.disconnect();
      this.#drawerObserver = null;
      // 1–2 s random delay so it doesn't feel mechanical.
      const delay = 1000 + Math.random() * 1000;
      this.#retryTimer = setTimeout(() => {
        this.#retryTimer = null;
        this.#open();
      }, delay);
    });

    this.#drawerObserver.observe(document.body, {
      subtree: true,
      attributes: true,
      attributeFilter: ['open'],
    });
  }

  #open() {
    if (this.#isAnyDrawerOpen()) {
      this.#scheduleRetryAfterDrawerClose();
      return;
    }
    this.#dialogComponent?.showDialog();
  }

  #isWithinCooldown() {
    if (this.dataset.debugAlwaysShow === 'true') return false;
    const days = Number(this.dataset.cooldownDays ?? 7);
    if (days <= 0) return false;
    const last = Number(localStorage.getItem(this.#cooldownKey) || 0);
    if (!last) return false;
    return Date.now() - last < days * 24 * 60 * 60 * 1000;
  }

  #writeCooldown() {
    if (this.dataset.debugAlwaysShow === 'true') return;
    localStorage.setItem(this.#cooldownKey, String(Date.now()));
  }

  #bindDelay() {
    const ms = Math.max(0, Number(this.dataset.delaySeconds ?? 5)) * 1000;
    const id = setTimeout(() => this.#open(), ms);
    this.#unbindTrigger = () => clearTimeout(id);
  }

  #bindScroll() {
    const target = Number(this.dataset.scrollPercent ?? 40);
    const handler = () => {
      const pct = ((window.scrollY + window.innerHeight) / document.body.scrollHeight) * 100;
      if (pct < target) return;
      window.removeEventListener('scroll', handler);
      this.#unbindTrigger = null;
      this.#open();
    };
    window.addEventListener('scroll', handler, { passive: true });
    this.#unbindTrigger = () => window.removeEventListener('scroll', handler);
  }

  #bindExitIntent() {
    const handler = (/** @type {MouseEvent} */ event) => {
      if (event.clientY > 0) return;
      document.removeEventListener('mouseleave', handler);
      this.#unbindTrigger = null;
      this.#open();
    };
    document.addEventListener('mouseleave', handler);
    this.#unbindTrigger = () => document.removeEventListener('mouseleave', handler);
  }

  #bindManual() {
    const handler = () => {
      document.removeEventListener('ss-newsletter:open', handler);
      this.#unbindTrigger = null;
      this.#open();
    };
    document.addEventListener('ss-newsletter:open', handler);
    this.#unbindTrigger = () => document.removeEventListener('ss-newsletter:open', handler);
  }
}

if (!customElements.get('ss-modal-popup')) {
  customElements.define('ss-modal-popup', SsModalPopup);
}
